/**
 * Futures Symbol Detail Page
 *
 * Displays real-time orderbook data and price differentials for a specific symbol
 * across multiple spot exchanges (Upbit, Bithumb, Binance) compared to futures.
 *
 * Route: /futures/{symbol} (e.g., /futures/BTCUSDT)
 *
 * Features:
 * - Real-time futures orderbook and mark price
 * - Real-time spot orderbooks from multiple exchanges
 * - Price differential calculations (futures vs spot)
 * - Launch strategy execution per exchange
 */

import React, { useState, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Stack,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BlockIcon from '@mui/icons-material/Block';
import { useMarkPrice } from '../hooks/useMarkPrice';
import { useFuturesOrderbookSubscription } from '../hooks/useFuturesOrderbookSubscription';
import { useMultiExchangeOrderbook } from '../hooks/useMultiExchangeOrderbook';
import { usePriceUpdates } from '../hooks/usePriceUpdates';
import { StrategyExecutionDialog } from '../components/OpportunityTable/StrategyExecutionDialog';
import { StandbyHedgedConfirmDialog } from '../components/OpportunityTable/StandbyHedgedConfirmDialog';
import { useTraderName } from '../contexts/TraderNameContext';
import { HeaderStats } from '../components/HeaderStats';
import { FundingCountdown } from '../components/FuturesSpotTable/FundingCountdown';
import { getExchangeLogo } from '../utils/exchangeUrls';
import { formatPrice, formatPercentage } from '../utils/formatters';
import { updateSettings } from '../features/settings/settingsSlice';
import {
  calculateAllSpotArbitrageOpportunities,
  formatCurrency,
  getProfitColor,
  type SpotArbitrageOpportunity,
} from '../utils/spotArbitrageCalculations';
import { executeStrategy, executeStrategyWithPayload } from '../api/strategies';
import type { RootState } from '../app/store';
import type { ArbitrageOpportunity } from '../types/opportunity';
import type { OrderbookResponse } from '../types/orderbook';

interface ExchangeCardProps {
  exchangeName: string;
  exchangeIndex: number;
  orderbook: OrderbookResponse | null;
  futuresPrice: number | null;
  loading: boolean;
  error: string | null;
  onLaunch: () => void;
  krwToUsdtRate: number | null;  // KRW/USDT conversion rate for Korean exchanges
  quoteCurrency: 'K' | 'U';  // Quote currency for price formatting
}

const ExchangeCard: React.FC<ExchangeCardProps> = ({
  exchangeName,
  exchangeIndex,
  orderbook,
  futuresPrice,
  loading,
  error,
  onLaunch,
  krwToUsdtRate,
  quoteCurrency,
}) => {
  // Calculate best ask (where we'd buy on spot)
  const bestAsk = orderbook?.asks && orderbook.asks.length > 0 ? orderbook.asks[0] : null;
  const rawSpotPrice = bestAsk?.price ?? null;

  // For price differential calculation, convert KRW to USDT
  let spotPriceInUsdt = rawSpotPrice;
  const isKoreanExchange = exchangeIndex === 0 || exchangeIndex === 1;
  if (isKoreanExchange && rawSpotPrice !== null && krwToUsdtRate !== null) {
    spotPriceInUsdt = rawSpotPrice / krwToUsdtRate;  // Convert KRW to USDT for comparison
  }

  // Calculate real diff: futures - spot (both in USDT now)
  const diff = futuresPrice !== null && spotPriceInUsdt !== null ? futuresPrice - spotPriceInUsdt : null;
  const diffPercent = spotPriceInUsdt && diff !== null ? (diff / spotPriceInUsdt) * 100 : null;

  // Color based on diff (positive = futures higher = potential arb)
  const diffColor = diffPercent !== null
    ? diffPercent > 0
      ? 'success.main'
      : diffPercent < 0
      ? 'error.main'
      : 'text.secondary'
    : 'text.secondary';

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(15,18,28,0.95) 0%, rgba(9,12,20,0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Stack spacing={2}>
        {/* Exchange Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              src={getExchangeLogo(exchangeIndex)}
              sx={{ width: 40, height: 40 }}
            />
            <Typography variant="h6" fontWeight="bold">
              {exchangeName}
            </Typography>
          </Stack>
          {orderbook && (
            <Chip
              label="Live"
              color="success"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>

        {/* Loading State */}
        {loading && !orderbook && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Error State - Show icon for unsupported symbols */}
        {error && !orderbook && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <Tooltip title={`${exchangeName} does not support this symbol`}>
              <BlockIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            </Tooltip>
          </Box>
        )}

        {/* Orderbook Data */}
        {orderbook && (
          <>
            {/* Spot Price */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Spot Best Ask
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {rawSpotPrice !== null ? formatPrice(rawSpotPrice, quoteCurrency) : '—'}
              </Typography>
            </Box>

            {/* Futures vs Spot Percentage Differential */}
            {diffPercent !== null && (
              <Box>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: diffColor }}
                >
                  {diffPercent >= 0 ? '+' : ''}{diffPercent.toFixed(2)}%
                </Typography>
              </Box>
            )}

            {/* Ask Levels (lowest 5, displayed from 5th to 1st) */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Top 5 Ask Levels
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {[...orderbook.asks]
                  .sort((a, b) => a.price - b.price)
                  .slice(0, 5)
                  .reverse()
                  .map((level, idx, arr) => (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                        background: idx === arr.length - 1 ? 'rgba(255,0,0,0.1)' : 'transparent',
                      }}
                    >
                      <Typography variant="body2" fontWeight={idx === arr.length - 1 ? 600 : 400}>
                        {formatPrice(level.price, quoteCurrency)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {level.volume.toFixed(4)}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </Box>

            {/* Launch Button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RocketLaunchIcon />}
              onClick={onLaunch}
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                },
              }}
            >
              Launch Strategy
            </Button>

            {/* Bid Levels (descending - highest to lowest) */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Top 5 Bid Levels
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {[...orderbook.bids]
                  .sort((a, b) => b.price - a.price)
                  .slice(0, 5)
                  .map((level, idx) => (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {formatPrice(level.price, quoteCurrency)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {level.volume.toFixed(4)}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export const FuturesSymbolDetailPage: React.FC = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { settings } = useSelector((state: RootState) => state.settings);
  const traderName = useTraderName();
  const [selectedOpportunity, setSelectedOpportunity] = useState<ArbitrageOpportunity | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [budgetInput, setBudgetInput] = useState(settings.strategyBudget.toString());
  const [profitInput, setProfitInput] = useState((settings.profitTiers[0].profit_percentage * 100).toString());

  // StandbyHedged confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState<{ name: string; index: number } | null>(null);
  const [selectedSpotOrderbook, setSelectedSpotOrderbook] = useState<OrderbookResponse | null>(null);

  // Extract base asset and normalize symbol
  // symbol can be either "BTC" or "BTCUSDT" - we normalize to base asset
  const baseAsset = symbol ? symbol.replace(/USDT$/i, '').toUpperCase() : '';
  const fullSymbol = baseAsset ? `${baseAsset}USDT` : '';
  const normalizedSymbol = baseAsset ? `${baseAsset}/USDT` : '';

  // Fetch futures data
  const markPriceData = useMarkPrice(normalizedSymbol);
  const futuresOrderbookState = useFuturesOrderbookSubscription(fullSymbol, !!symbol);
  const futuresOrderbook = futuresOrderbookState.orderbook;

  // Fetch spot orderbooks from all exchanges
  const spotOrderbooks = useMultiExchangeOrderbook({
    symbol: fullSymbol,
    autoRefresh: true,
    enabled: !!symbol,
  });

  // Get KRW/USDT rate for price conversion
  const priceData = usePriceUpdates();
  const krwToUsdtRate = priceData?.upbitUsdtKrw ?? null;  // Use Upbit's USDT/KRW rate

  // Futures price (use last price from mark price data)
  const futuresPrice = markPriceData?.lastPrice ?? null;

  // Debug: Log mark price data to see if nextFundingTime exists
  React.useEffect(() => {
    console.log('[FuturesSymbolDetailPage] Mark price data:', {
      symbol: normalizedSymbol,
      markPriceData,
      nextFundingTime: markPriceData?.nextFundingTime,
      fundingRate: markPriceData?.fundingRate,
    });
  }, [markPriceData, normalizedSymbol]);

  // Create mock opportunity for StrategyExecutionDialog
  const createMockOpportunity = (
    exchangeName: string,
    exchangeIndex: number,
    spotPrice: number
  ): ArbitrageOpportunity => {
    // Simplified opportunity structure - StrategyExecutionDialog will handle the details
    return {
      buy_exchange: exchangeIndex,
      sell_exchange: 0, // Binance Futures
      base: baseAsset,
      buy_quote: 'U', // USDT
      sell_quote: 'U',
      buy_price: spotPrice,
      sell_price: futuresPrice ?? spotPrice,
      buy_volume: 0,
      sell_volume: 0,
      max_volume: 0,
      profit_ratio: futuresPrice && spotPrice ? futuresPrice / spotPrice : 1,
      buy_fee: 0.001,
      sell_fee: 0.0004,
      detected_at: Date.now(),
      updated_at: Date.now(),
      buy_levels: [],
      sell_levels: [],
    };
  };

  // Sync profitInput with settings when settings change externally
  React.useEffect(() => {
    setProfitInput((settings.profitTiers[0].profit_percentage * 100).toString());
  }, [settings.profitTiers]);

  // Handle budget change
  const handleBudgetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBudgetInput(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      dispatch(updateSettings({
        ...settings,
        strategyBudget: numValue,
      }));
    }
  };

  // Handle launch button click from spot orderbook cards
  // Opens StandbyHedged confirmation dialog (NOT the regular strategy dialog)
  const handleLaunch = (exchangeName: string, exchangeIndex: number, orderbook: OrderbookResponse | null) => {
    if (!orderbook || !futuresOrderbook) {
      console.error('[handleLaunch] Cannot launch: orderbooks not available', {
        hasSpotOrderbook: !!orderbook,
        hasFuturesOrderbook: !!futuresOrderbook,
      });
      return;
    }

    console.log('[handleLaunch] Opening StandbyHedged dialog', {
      exchangeName,
      exchangeIndex,
    });

    // Set state for the dialog
    setSelectedExchange({ name: exchangeName, index: exchangeIndex });
    setSelectedSpotOrderbook(orderbook);
    setConfirmDialogOpen(true);
  };

  // Calculate spot-to-spot arbitrage opportunities
  const spotArbitrageOpportunities = useMemo(() => {
    // Prepare exchange data for calculation
    const exchanges = [
      {
        name: 'Upbit',
        index: 0,
        orderbook: spotOrderbooks.upbit.orderbook,
        quoteCurrency: 'K' as const,
      },
      {
        name: 'Bithumb',
        index: 1,
        orderbook: spotOrderbooks.bithumb.orderbook,
        quoteCurrency: 'K' as const,
      },
      {
        name: 'Binance',
        index: 3,
        orderbook: spotOrderbooks.binance.orderbook,
        quoteCurrency: 'U' as const,
      },
    ];

    return calculateAllSpotArbitrageOpportunities(
      exchanges,
      settings.strategyBudget,
      krwToUsdtRate
    );
  }, [spotOrderbooks, settings.strategyBudget, krwToUsdtRate]);

  // Handle launch for spot arbitrage opportunity
  const handleLaunchSpotArbitrage = (opportunity: SpotArbitrageOpportunity) => {
    // Find the corresponding spot orderbook based on buy exchange
    let spotOrderbook: OrderbookResponse | null = null;
    if (opportunity.buyExchangeIndex === 0) {
      spotOrderbook = spotOrderbooks.upbit.orderbook;
    } else if (opportunity.buyExchangeIndex === 1) {
      spotOrderbook = spotOrderbooks.bithumb.orderbook;
    } else if (opportunity.buyExchangeIndex === 3) {
      spotOrderbook = spotOrderbooks.binance.orderbook;
    }

    if (!spotOrderbook) {
      console.error('Cannot launch: spot orderbook not available');
      return;
    }

    // Store the opportunity and orderbook for the confirmation dialog
    setSelectedSpotOpp(opportunity);
    setSelectedSpotOrderbook(spotOrderbook);
    setConfirmDialogOpen(true);
  };

  // Handle StandbyHedged strategy execution
  // TODO: This page needs trader context - will be deprecated
  // For now, use 'njovy' as default trader name
  const handleExecuteStandbyHedged = async (payload: any) => {
    await executeStrategyWithPayload(payload, 'njovy');
  };

  if (!symbol) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          <Typography variant="h6">Symbol parameter is required</Typography>
          <Typography variant="body2">
            Please provide a symbol in the URL (e.g., /futures/BTCUSDT)
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            component={RouterLink}
            to="/futures"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            size="small"
          >
            Back
          </Button>
          <Typography variant="h3" fontWeight="bold">
            {symbol}
          </Typography>
        </Stack>
        <Tooltip title="Refresh all data">
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Header Statistics Bar - BTC prices and market stats */}
      <HeaderStats />

      {/* Budget and Profit Tiers Input */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          maxWidth: 600,
          mx: 'auto',
          background: 'linear-gradient(135deg, rgba(15,18,28,0.95) 0%, rgba(9,12,20,0.98) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Strategy Configuration
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Single
            </Typography>
            <Switch
              checked={settings.useMultiTierProfit}
              onChange={(e) => {
                dispatch(updateSettings({
                  ...settings,
                  useMultiTierProfit: e.target.checked,
                }));
              }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'primary.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Multi
            </Typography>
          </Stack>
        </Stack>

        {/* Budget Input */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ minWidth: 60 }}>
            Budget
          </Typography>
          <TextField
            label="USD"
            type="number"
            value={budgetInput}
            onChange={handleBudgetChange}
            size="small"
            inputProps={{
              min: 0,
              step: 0.1,
            }}
            sx={{
              width: 316,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Stack>

        {/* Profit Tiers */}
        <Stack spacing={2}>
          {/* Single Tier Mode */}
          {!settings.useMultiTierProfit && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ minWidth: 60 }}>
                Target
              </Typography>
              <TextField
                label="Profit %"
                type="number"
                value={profitInput}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log('[Profit Input] Raw value:', value);
                  setProfitInput(value);

                  const numValue = parseFloat(value);
                  console.log('[Profit Input] Parsed value:', numValue);

                  if (!isNaN(numValue) && numValue >= 0) {
                    // DEEP copy of tiers - Redux state is immutable!
                    const newTiers = settings.profitTiers.map((tier, index) =>
                      index === 0
                        ? {
                            profit_percentage: numValue / 100,
                            volume_percentage_to_sell: 1.0,  // Always 100% for single tier
                          }
                        : { ...tier }
                    );
                    console.log('[Profit Input] Dispatching new tiers:', newTiers);
                    dispatch(updateSettings({
                      ...settings,
                      profitTiers: newTiers,
                    }));
                  }
                }}
                size="small"
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
                sx={{
                  width: 150,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <TextField
                label="Sell Volume %"
                type="number"
                value="100"
                disabled
                size="small"
                sx={{
                  width: 150,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                  },
                }}
              />
            </Stack>
          )}

          {/* Multi Tier Mode */}
          {settings.useMultiTierProfit && settings.profitTiers.map((tier, index) => (
            <Stack direction="row" spacing={2} alignItems="center" key={index}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ minWidth: 60 }}>
                Tier {index + 1}
              </Typography>
              <TextField
                label="Profit %"
                type="number"
                value={tier.profit_percentage * 100}
                onChange={(e) => {
                  // DEEP copy - Redux state is immutable!
                  const newTiers = settings.profitTiers.map((t, i) =>
                    i === index
                      ? { ...t, profit_percentage: parseFloat(e.target.value) / 100 }
                      : { ...t }
                  );
                  dispatch(updateSettings({
                    ...settings,
                    profitTiers: newTiers,
                  }));
                }}
                size="small"
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
                sx={{
                  width: 150,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <TextField
                label="Sell Volume %"
                type="number"
                value={tier.volume_percentage_to_sell * 100}
                onChange={(e) => {
                  // DEEP copy - Redux state is immutable!
                  const newTiers = settings.profitTiers.map((t, i) =>
                    i === index
                      ? { ...t, volume_percentage_to_sell: parseFloat(e.target.value) / 100 }
                      : { ...t }
                  );
                  dispatch(updateSettings({
                    ...settings,
                    profitTiers: newTiers,
                  }));
                }}
                size="small"
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 1,
                }}
                sx={{
                  width: 150,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Stack>
          ))}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {settings.useMultiTierProfit
            ? 'Configure multiple profit targets with corresponding volume percentages to sell at each tier'
            : 'Configure a single profit target and volume percentage to sell when reached'}
        </Typography>
      </Paper>

      {/* All Orderbooks in 4-column Grid */}
      <Grid container spacing={3} justifyContent="center">
        {/* Binance Futures - First Column */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(25,118,210,0.15) 0%, rgba(66,165,245,0.1) 100%)',
              border: '1px solid rgba(25,118,210,0.4)',
            }}
          >
            <Stack spacing={2}>
              {/* Exchange Header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    src={getExchangeLogo(3)}
                    sx={{ width: 40, height: 40 }}
                  />
                  <Typography variant="h6" fontWeight="bold">
                    Binance Futures
                  </Typography>
                </Stack>
                {futuresOrderbook && (
                  <Chip
                    label="Live"
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Stack>

              {/* Futures Price */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Futures Best Bid
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {futuresOrderbook?.bids?.[0]?.price
                    ? formatPrice(futuresOrderbook.bids[0].price, 'U')
                    : futuresPrice !== null
                    ? formatPrice(futuresPrice, 'U')
                    : '—'}
                </Typography>
              </Box>

              {/* Funding Rate with Countdown */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Funding Rate
                </Typography>
                <Stack direction="column" alignItems="center" spacing={1}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                      color: markPriceData?.fundingRate !== null && markPriceData?.fundingRate !== undefined
                        ? markPriceData.fundingRate >= 0
                          ? 'success.main'  // Green for positive
                          : 'warning.main'  // Orange for negative
                        : 'text.primary'
                    }}
                  >
                    {markPriceData?.fundingRate !== null && markPriceData?.fundingRate !== undefined
                      ? `${markPriceData.fundingRate >= 0 ? '+' : ''}${(markPriceData.fundingRate * 100).toFixed(4)}%`
                      : '—'}
                  </Typography>
                  {markPriceData?.nextFundingTime && (
                    <FundingCountdown nextFundingTime={markPriceData.nextFundingTime} />
                  )}
                </Stack>
              </Box>

              {/* Bid Levels (descending - highest to lowest) */}
              {futuresOrderbook?.bids && futuresOrderbook.bids.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Top 5 Bid Levels
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {[...futuresOrderbook.bids]
                      .sort((a, b) => b.price - a.price)
                      .slice(0, 5)
                      .map((level, idx) => (
                        <Stack
                          key={idx}
                          direction="row"
                          justifyContent="space-between"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 0.5,
                            background: idx === 0 ? 'rgba(0,255,0,0.1)' : 'transparent',
                          }}
                        >
                          <Typography variant="body2" fontWeight={idx === 0 ? 600 : 400}>
                            {formatPrice(level.price, 'U')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {level.volume.toFixed(4)}
                          </Typography>
                        </Stack>
                      ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Upbit - Second Column */}
        <Grid item xs={12} md={3}>
          <ExchangeCard
            exchangeName="Upbit"
            exchangeIndex={0}
            orderbook={spotOrderbooks.upbit.orderbook}
            futuresPrice={futuresPrice}
            loading={spotOrderbooks.upbit.loading}
            error={spotOrderbooks.upbit.error}
            onLaunch={() => handleLaunch('Upbit', 0, spotOrderbooks.upbit.orderbook)}
            krwToUsdtRate={krwToUsdtRate}
            quoteCurrency="K"
          />
        </Grid>

        {/* Bithumb - Third Column */}
        <Grid item xs={12} md={3}>
          <ExchangeCard
            exchangeName="Bithumb"
            exchangeIndex={1}
            orderbook={spotOrderbooks.bithumb.orderbook}
            futuresPrice={futuresPrice}
            loading={spotOrderbooks.bithumb.loading}
            error={spotOrderbooks.bithumb.error}
            onLaunch={() => handleLaunch('Bithumb', 1, spotOrderbooks.bithumb.orderbook)}
            krwToUsdtRate={krwToUsdtRate}
            quoteCurrency="K"
          />
        </Grid>

        {/* Binance Spot - Fourth Column */}
        <Grid item xs={12} md={3}>
          <ExchangeCard
            exchangeName="Binance"
            exchangeIndex={3}
            orderbook={spotOrderbooks.binance.orderbook}
            futuresPrice={futuresPrice}
            loading={spotOrderbooks.binance.loading}
            error={spotOrderbooks.binance.error}
            onLaunch={() => handleLaunch('Binance', 3, spotOrderbooks.binance.orderbook)}
            krwToUsdtRate={krwToUsdtRate}
            quoteCurrency="U"
          />
        </Grid>
      </Grid>

      {/* Spot Arbitrage Opportunities Table */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Spot Arbitrage Opportunities
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Calculated arbitrage opportunities between spot exchanges using budget of {formatCurrency(settings.strategyBudget)}
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            background: 'linear-gradient(135deg, rgba(15,18,28,0.95) 0%, rgba(9,12,20,0.98) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Direction</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Buy Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Sell Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Volume ({baseAsset})</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Total Cost</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Total Revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Trading Fees</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Withdrawal Fee</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Net Profit</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Profit %</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {spotArbitrageOpportunities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No profitable arbitrage opportunities found with current budget and orderbook depth
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                spotArbitrageOpportunities.map((opp, index) => {
                  const profitColor = getProfitColor(opp.profitPercentage);
                  const isPositive = opp.netProfitUsdt > 0;

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        '&:hover': {
                          background: 'rgba(255,255,255,0.03)',
                        },
                        opacity: isPositive ? 1 : 0.6,
                      }}
                    >
                      {/* Direction */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            src={getExchangeLogo(opp.buyExchangeIndex)}
                            sx={{ width: 24, height: 24 }}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            →
                          </Typography>
                          <Avatar
                            src={getExchangeLogo(opp.sellExchangeIndex)}
                            sx={{ width: 24, height: 24 }}
                          />
                        </Stack>
                      </TableCell>

                      {/* Buy Price */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(opp.buyPriceUsdt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({opp.buyQuoteCurrency === 'K' ? 'KRW' : 'USDT'})
                        </Typography>
                      </TableCell>

                      {/* Sell Price */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(opp.sellPriceUsdt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({opp.sellQuoteCurrency === 'K' ? 'KRW' : 'USDT'})
                        </Typography>
                      </TableCell>

                      {/* Volume */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {opp.maxExecutableVolume.toFixed(6)}
                        </Typography>
                      </TableCell>

                      {/* Total Cost */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(opp.totalBuyCostUsdt)}
                        </Typography>
                      </TableCell>

                      {/* Total Revenue */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(opp.totalSellRevenueUsdt)}
                        </Typography>
                      </TableCell>

                      {/* Trading Fees */}
                      <TableCell align="right">
                        <Typography variant="body2" color="warning.main">
                          {formatCurrency(opp.buyFeeAmountUsdt + opp.sellFeeAmountUsdt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Buy: {(opp.buyFeeRate * 100).toFixed(2)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sell: {(opp.sellFeeRate * 100).toFixed(2)}%
                        </Typography>
                      </TableCell>

                      {/* Withdrawal Fee */}
                      <TableCell align="right">
                        <Typography variant="body2" color="warning.main">
                          {formatCurrency(opp.withdrawalFeeAmountUsdt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({(opp.withdrawalFeeRate * 100).toFixed(2)}%)
                        </Typography>
                      </TableCell>

                      {/* Net Profit */}
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={isPositive ? 'success.main' : 'error.main'}
                        >
                          {isPositive ? '+' : ''}{formatCurrency(opp.netProfitUsdt)}
                        </Typography>
                      </TableCell>

                      {/* Profit % */}
                      <TableCell align="right">
                        <Chip
                          label={`${isPositive ? '+' : ''}${opp.profitPercentage.toFixed(2)}%`}
                          color={profitColor}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>

                      {/* Action */}
                      <TableCell align="center">
                        {isPositive ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<RocketLaunchIcon />}
                            onClick={() => handleLaunchSpotArbitrage(opp)}
                            sx={{
                              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                              },
                            }}
                          >
                            Launch
                          </Button>
                        ) : (
                          <Chip
                            label="Not Profitable"
                            size="small"
                            sx={{ opacity: 0.5 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Strategy Execution Dialog */}
      {selectedOpportunity && (
        <StrategyExecutionDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          opportunity={selectedOpportunity}
          traderName={traderName}
        />
      )}

      {/* StandbyHedged Confirmation Dialog */}
      {confirmDialogOpen && (
        <StandbyHedgedConfirmDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleExecuteStandbyHedged}
          exchangeName={selectedExchange?.name || ''}
          exchangeIndex={selectedExchange?.index || 0}
          quoteCurrency={selectedExchange && (selectedExchange.index === 0 || selectedExchange.index === 1) ? 'K' : 'U'}
          baseAsset={baseAsset}
          spotOrderbook={selectedSpotOrderbook}
          futuresOrderbook={futuresOrderbook}
          settings={settings}
        />
      )}
    </Container>
  );
};

export default FuturesSymbolDetailPage;
